import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Grid, FormControl, InputLabel, Select, MenuItem,
  TableRow, TableCell, Typography, useTheme, LinearProgress,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  GlassCard, GlassTable, ShimmerSkeleton, EmptyState,
} from '../../../components/admin/ui';
import { makeAnalyticsApi } from '../../../analytics/adminApi';
import { ADMIN_COLORS } from '../../../components/admin/ui/glassTokens';

// ─── Dimension options ────────────────────────────────────────────────────────

const DIMENSION_OPTIONS = [
  { value: 'country', label: 'Country' },
  { value: 'state', label: 'State' },
  { value: 'city', label: 'City' },
  { value: 'device', label: 'Device' },
  { value: 'browser', label: 'Browser' },
  { value: 'os', label: 'OS' },
];

// ─── Map defaults: center on India ───────────────────────────────────────────

const MAP_CENTER = [20.5937, 78.9629];
const MAP_ZOOM = 4;

function geoCircleRadius(count, maxCount) {
  if (!maxCount) return 6;
  return Math.max(5, Math.min(30, Math.round((count / maxCount) * 28) + 5));
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AnalyticsAudience({ filters, apiBase = '/admin/analytics' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [dimension, setDimension] = useState('country');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [geo, setGeo] = useState([]);

  const fetch = useCallback(() => {
    let cancelled = false;
    setLoading(true);

    const params = {
      from: filters.from,
      to: filters.to,
      dimension,
      newReturning: filters.newReturning || 'all',
      includeInternalRoles: filters.includeInternalRoles,
      includeBots: filters.includeBots,
    };

    const analyticsApi = makeAnalyticsApi(apiBase);
    analyticsApi.getAudience(params).then((d) => {
      if (!cancelled) {
        setRows(d.rows || []);
        setGeo(d.geo || []);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [filters.from, filters.to, filters.newReturning, filters.includeInternalRoles, filters.includeBots, dimension, apiBase]);

  useEffect(() => {
    const cancel = fetch();
    return cancel;
  }, [fetch]);

  const textColor = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)';
  const chartColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.08)';

  // Top 10 for chart
  const chartData = rows.slice(0, 10).map((r) => ({
    name: r.value || '(unknown)',
    sessions: r.sessions || 0,
  }));

  const maxGeoCount = geo.reduce((acc, g) => Math.max(acc, g.count || 0), 0);

  return (
    <Box>
      {/* ── Dimension selector ─────────────────────────────────────────── */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Break down by</InputLabel>
          <Select
            value={dimension}
            onChange={(e) => setDimension(e.target.value)}
            label="Break down by"
          >
            {DIMENSION_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2}>
        {/* ── Table ─────────────────────────────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <GlassCard sx={{ p: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 2, color: isDark ? '#F8FAFC' : '#0F172A' }}
            >
              {DIMENSION_OPTIONS.find((o) => o.value === dimension)?.label || 'Dimension'} Breakdown
            </Typography>
            {loading ? (
              <ShimmerSkeleton height={300} />
            ) : rows.length === 0 ? (
              <EmptyState title="No data" description="No audience data for the selected period." />
            ) : (
              <GlassTable
                columns={[
                  { label: DIMENSION_OPTIONS.find((o) => o.value === dimension)?.label || 'Value' },
                  { label: 'Sessions', align: 'right' },
                  { label: 'Uniques', align: 'right' },
                  { label: 'Share', align: 'right', width: 80 },
                ]}
                sx={{ maxHeight: 380 }}
              >
                {rows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.value || '(unknown)'}
                    </TableCell>
                    <TableCell align="right">{(row.sessions || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{(row.uniques || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, (row.pct || 0) * 100)}
                          sx={{
                            width: 40,
                            height: 4,
                            borderRadius: 2,
                            bgcolor: chartColor,
                            '& .MuiLinearProgress-bar': {
                              bgcolor: ADMIN_COLORS.accentIndigo,
                              borderRadius: 2,
                            },
                          }}
                        />
                        <Box component="span" sx={{ fontSize: '0.75rem', minWidth: 32, textAlign: 'right' }}>
                          {((row.pct || 0) * 100).toFixed(1)}%
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </GlassTable>
            )}
          </GlassCard>
        </Grid>

        {/* ── Bar chart ─────────────────────────────────────────────────── */}
        <Grid item xs={12} md={7}>
          <GlassCard sx={{ p: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 2, color: isDark ? '#F8FAFC' : '#0F172A' }}
            >
              Top 10 by Sessions
            </Typography>
            {loading ? (
              <ShimmerSkeleton height={220} />
            ) : chartData.length === 0 ? (
              <EmptyState title="No chart data" iconIndex={1} />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColor} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: textColor }} tickLine={false} axisLine={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: textColor }}
                    tickLine={false}
                    axisLine={false}
                    width={90}
                  />
                  <Tooltip
                    contentStyle={{
                      background: isDark ? 'rgba(15,18,32,0.92)' : '#fff',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)'}`,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="sessions" fill={ADMIN_COLORS.accentIndigo} radius={[0, 4, 4, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </GlassCard>

          {/* ── Leaflet geo map ───────────────────────────────────────── */}
          <GlassCard sx={{ p: 3, mt: 2 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 2, color: isDark ? '#F8FAFC' : '#0F172A' }}
            >
              Geographic Distribution
            </Typography>
            {loading ? (
              <ShimmerSkeleton height={300} />
            ) : (
              <Box sx={{ height: 300, borderRadius: 1, overflow: 'hidden' }}>
                <MapContainer
                  center={MAP_CENTER}
                  zoom={MAP_ZOOM}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    url={
                      isDark
                        ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                        : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
                    }
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                  />
                  {geo
                    .filter((g) => g.lat != null && g.lng != null)
                    .map((g, i) => (
                      <CircleMarker
                        key={i}
                        center={[g.lat, g.lng]}
                        radius={geoCircleRadius(g.count, maxGeoCount)}
                        pathOptions={{
                          fillColor: ADMIN_COLORS.accentIndigo,
                          fillOpacity: 0.65,
                          color: ADMIN_COLORS.accentViolet,
                          weight: 1,
                        }}
                      >
                        <LeafletTooltip>
                          <strong>{g.city || g.state || g.country}</strong>
                          <br />
                          {(g.count || 0).toLocaleString()} sessions
                        </LeafletTooltip>
                      </CircleMarker>
                    ))}
                </MapContainer>
              </Box>
            )}
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );
}

AnalyticsAudience.propTypes = {
  filters: PropTypes.object.isRequired,
  apiBase: PropTypes.string,
};
